import {
  AnswerBookSingleTitleSubscriptionVariation,
  AnswerBookVariation,
  BookProduct,
  CourseHandbookSingleTitleSubscriptionVariation,
  CourseHandbookVariation,
  TreatiseSingleTitleSubscriptionVariation,
  TreatiseVariation
} from '@/@types/content';
import { ProgramVariation } from './useProgramCatalogRelations';
import { useGetContentQuery } from '@/redux/api/contentDeliveryAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import { IContent } from '@/@types/cms';
import { isOfContentType } from '@/lib/helpers/contentType';
import { getExpiredStatus } from '@/components/ui/PDP/helpers';
import { useGetAllChildren } from '../useGetAllChildren';
import { useProductContentQuery } from './useProductContentQuery';
import { isDefined } from '@/lib/helpers/isDefined';
import { useMemo } from 'react';

type ABTreatisePrintVariation = AnswerBookVariation | TreatiseVariation;
type ABTreatiseSTSVariation =
  | AnswerBookSingleTitleSubscriptionVariation
  | TreatiseSingleTitleSubscriptionVariation;
export type ABTreatiseVariation = ABTreatisePrintVariation | ABTreatiseSTSVariation;

export type PublicationCatalogRelations = ABTreatiseCatalogRelations | CHBCatalogRelations;
export type PublicationVariation = ABTreatiseVariation | CHBVariation;
export type DigitalAccessVariation = PublicationVariation & {
  plusAccessSource?: 'program' | 'membership';
};
export type ABTreatiseCatalogRelations = {
  bookProduct?: BookProduct;
  printVariants: ABTreatisePrintVariation[];
  expiredPrintVariants?: ABTreatisePrintVariation[];
  stsVariant?: ABTreatiseSTSVariation;
  digitalAccessVariant?: DigitalAccessVariation;
  pseudoStrikePrice?: number;
  isPlusSubscriber?: boolean;
  isLoading?: boolean;
  isError?: boolean;
};

enum BOOK_CONTENT_TYPES {
  AnswerBookVariation = 'AnswerBookVariation',
  TreatiseVariation = 'TreatiseVariation',
  AnswerBookSTSVariation = 'AnswerBookSingleTitleSubscriptionVariation',
  TreatiseSTSVariation = 'TreatiseSingleTitleSubscriptionVariation',
  CourseHandbookVariation = 'CourseHandbookVariation',
  CourseHandbookSTSVariation = 'CourseHandbookSingleTitleSubscriptionVariation'
}

const ABTreatisePrintContentTypes = [
  BOOK_CONTENT_TYPES.AnswerBookVariation,
  BOOK_CONTENT_TYPES.TreatiseVariation
];
export const isABTreatisePrintContentType = (
  content: IContent
): content is ABTreatisePrintVariation => isOfContentType(content, ABTreatisePrintContentTypes);

export const isTreatisePrintContentType = (content: IContent) =>
  isOfContentType(content, [BOOK_CONTENT_TYPES.TreatiseVariation]);

const ABTreatiseSTSContentTypes = [
  BOOK_CONTENT_TYPES.AnswerBookSTSVariation,
  BOOK_CONTENT_TYPES.TreatiseSTSVariation
];
export const isABTreatiseSTSContentType = (content: IContent): content is ABTreatiseSTSVariation =>
  isOfContentType(content, ABTreatiseSTSContentTypes);
export const isABTreatiseContentType = (content: IContent): content is ABTreatiseVariation =>
  isABTreatisePrintContentType(content) || isABTreatiseSTSContentType(content);

const ABContentTypes = [
  BOOK_CONTENT_TYPES.AnswerBookVariation,
  BOOK_CONTENT_TYPES.AnswerBookSTSVariation
];
const TreatiseContentTypes = [
  BOOK_CONTENT_TYPES.TreatiseVariation,
  BOOK_CONTENT_TYPES.TreatiseSTSVariation
];
export const isAnswerBookVariant = (content: IContent) => isOfContentType(content, ABContentTypes);
export const isAnswerBookSTSVariant = (content: IContent) =>
  isOfContentType(content, [BOOK_CONTENT_TYPES.AnswerBookSTSVariation]);
export const isTreatiseVariant = (content: IContent) =>
  isOfContentType(content, TreatiseContentTypes);
export const isTreatiseSTSVariant = (content: IContent) =>
  isOfContentType(content, [BOOK_CONTENT_TYPES.TreatiseSTSVariation]);
export const isCHBPrintVariant = (c: IContent) =>
  isOfContentType(c, [BOOK_CONTENT_TYPES.CourseHandbookVariation]);
export const isCHBSTSVariant = (c: IContent) =>
  isOfContentType(c, [BOOK_CONTENT_TYPES.CourseHandbookSTSVariation]);
export const isCHBVariant = (c: IContent) => isCHBPrintVariant(c) || isCHBSTSVariant(c);
export const isPrintVariant = (c: IContent) =>
  isABTreatisePrintContentType(c) || isCHBPrintVariant(c);
export const isBookVariant = (content: IContent) =>
  isAnswerBookVariant(content) || isTreatiseVariant(content) || isCHBVariant(content);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getBookProductLink = (c: CHBVariation | ABTreatiseVariation) => (c as any)?.bookProductLink;

const sortByLastUpdatedDateDesc = (a: ABTreatiseVariation, b: ABTreatiseVariation) => {
  const dateA = a.lastUpdatedDate ? new Date(a.lastUpdatedDate).getTime() : 0;
  const dateB = b.lastUpdatedDate ? new Date(b.lastUpdatedDate).getTime() : 0;
  if (dateA < dateB) return 1;
  if (dateA > dateB) return -1;
  return 0;
};

export const isPrerelease = (v: PublicationVariation) => {
  if (v.publicationDate && new Date(v.publicationDate) > new Date()) return true;
  return false;
};

const isSuppressedPreviousEdition = (
  latestUpdatedVariant: PublicationVariation,
  variant: PublicationVariation
) => {
  if (variant.code === latestUpdatedVariant.code) return false;
  // New editions are variants under same product as old editions.
  const dateToCompare = new Date(variant.lastUpdatedDate!);
  dateToCompare.setDate(dateToCompare.getDate() + 335);
  if (dateToCompare < new Date(latestUpdatedVariant.lastUpdatedDate!)) return true;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getVariantPrice = (variant?: IContent) => (variant as any)?.fullPrice;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getIsPlusSubscriber = (product?: IContent) => (product as any)?.isPlusSubscriber ?? false;

export const getABTreatisePks = (catalogRelations: ABTreatiseCatalogRelations) =>
  [
    ...(catalogRelations.printVariants?.map(v => v.code) ?? []),
    catalogRelations.stsVariant?.code
  ].filter(isDefined);
export const getCHBPks = (catalogRelations: CHBCatalogRelations) =>
  [catalogRelations.printVariant?.code, catalogRelations.stsVariant?.code].filter(isDefined);

const createDigitalAccessVariantIfEligible = (
  bookProduct?: BookProduct,
  printVariants?: PublicationVariation[],
  stsVariant?: PublicationVariation
): DigitalAccessVariation | undefined => {
  if (!bookProduct || (!printVariants && !stsVariant)) return undefined;

  if (!bookProduct.plusAvailable || !bookProduct.plusCode || !bookProduct.plusUrl) return undefined;
  // If user does not have digital access, do not create digital access variant
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasDigitalAccess = (bookProduct as any)?.hasPlusAccessFromProgramOrMembership === true;
  if (!hasDigitalAccess) return undefined;

  // If user has digital access, match the bookProduct plusCode to the variant
  // if plusCode can't be matched with the printVariants, fall back to sts variant, to support sts chb standalone.
  const plusCode = bookProduct.plusCode;
  const sourceVariant = printVariants?.find(v => v.code === plusCode) ?? stsVariant;
  if (!sourceVariant) return undefined;
  const hasDigitalAccessFromProgram =
    isCHBVariant(sourceVariant) && (bookProduct as any)?.hasPlusAccessFromProgram === true; // eslint-disable-line @typescript-eslint/no-explicit-any
  const digitalAccessUrl = `${sourceVariant.contentLink?.url}?plus`;
  const digitalAccessVariant = {
    ...sourceVariant,
    contentLink: { ...sourceVariant.contentLink, url: digitalAccessUrl },
    url: digitalAccessUrl,
    plusAccessSource: hasDigitalAccessFromProgram ? 'program' : 'membership'
  } as DigitalAccessVariation;
  return digitalAccessVariant;
};

export const useABTreatiseCatalogRelations = (
  variant?: ABTreatiseVariation
): ABTreatiseCatalogRelations => {
  const query = useProductContentQuery();
  const {
    data: bookProduct,
    isFetching: isBookProductFetching,
    isError: isContentError
  } = useGetContentQuery(variant ? { contentLink: getBookProductLink(variant), query } : skipToken);
  const {
    children,
    isLoading: isBookProductChildrenFetching,
    isApiError: isChildrenError
  } = useGetAllChildren({
    contentLink: bookProduct?.contentLink?.guidValue,
    enableWMSPrice: 'all'
  });

  // Published print editions
  const allPublishedPrintVariants = useMemo(
    () =>
      (children?.filter(c => isABTreatisePrintContentType(c) && !isPrerelease(c)) ??
        []) as ABTreatisePrintVariation[],
    [children]
  );

  // Future-published print editions
  const presaleVariants = useMemo(
    () =>
      (children?.filter(c => isABTreatisePrintContentType(c) && isPrerelease(c)) ??
        []) as ABTreatisePrintVariation[],
    [children]
  );

  // Latest published/updated print variant (not considering presales). It may be expired.
  // It should be used to create STS/Free digital variants, even if expired from print purchase.
  const referencePrintVariant = allPublishedPrintVariants?.sort(sortByLastUpdatedDateDesc)[0];

  // Print editions that are eligible for purchase in ecom box
  const activeEcommercePrintVariants = useMemo(
    () =>
      [
        ...presaleVariants,
        ...(allPublishedPrintVariants?.filter(
          v => !isSuppressedPreviousEdition(referencePrintVariant, v)
        ) ?? [])
      ].filter(v => !getExpiredStatus(v)),
    [allPublishedPrintVariants, presaleVariants, referencePrintVariant]
  );

  // Expired print editions
  const expiredEcommercePrintVariants = useMemo(
    () => allPublishedPrintVariants?.filter(v => getExpiredStatus(v)) ?? [],
    [allPublishedPrintVariants]
  );

  // For AB/Treatise, the STS variant is enriched with metadata from the most recently published/updated print variant
  const baseStsVariant = children?.find(isABTreatiseSTSContentType) as ABTreatiseSTSVariation;
  const stsVariant = useMemo(() => {
    return baseStsVariant && !getExpiredStatus(baseStsVariant) && referencePrintVariant
      ? {
          ...baseStsVariant,
          description: referencePrintVariant.description,
          author: referencePrintVariant.author,
          sortedTableOfContentListJsonString:
            referencePrintVariant.sortedTableOfContentListJsonString,
          publicationDate: referencePrintVariant.publicationDate,
          lastUpdatedDate: referencePrintVariant.lastUpdatedDate,
          volumeCount: referencePrintVariant.volumeCount,
          isbn: referencePrintVariant.isbn,
          pageCount: referencePrintVariant.pageCount
        }
      : undefined;
  }, [baseStsVariant, referencePrintVariant]);

  // The "strike price" for Free PLUS digital edition
  const pseudoStrikePrice = getVariantPrice(referencePrintVariant) ?? getVariantPrice(stsVariant);

  // Free Plus digital variant is created based on print variant, if book is available on PLUS and user has access from PLUS membership
  const digitalAccessVariant = useMemo(
    () =>
      bookProduct &&
      children &&
      createDigitalAccessVariantIfEligible(bookProduct, allPublishedPrintVariants, stsVariant),
    [allPublishedPrintVariants, bookProduct, children, stsVariant]
  );

  const catalogRelations = useMemo<ABTreatiseCatalogRelations>(
    () => ({
      bookProduct,
      printVariants: activeEcommercePrintVariants,
      expiredPrintVariants: expiredEcommercePrintVariants,
      stsVariant,
      digitalAccessVariant,
      pseudoStrikePrice,
      isPlusSubscriber: getIsPlusSubscriber(bookProduct),
      isLoading: isBookProductFetching || isBookProductChildrenFetching,
      isError: !!isContentError || !!isChildrenError
    }),
    [
      activeEcommercePrintVariants,
      expiredEcommercePrintVariants,
      bookProduct,
      digitalAccessVariant,
      isBookProductChildrenFetching,
      isBookProductFetching,
      isChildrenError,
      isContentError,
      pseudoStrikePrice,
      stsVariant
    ]
  );

  return catalogRelations;
};

export type CHBVariation = CourseHandbookVariation | CourseHandbookSingleTitleSubscriptionVariation;

export type CHBCatalogRelations = {
  bookProduct?: BookProduct;
  printVariant?: CourseHandbookVariation;
  expiredPrintVariants?: CHBVariation[];
  stsVariant?: CourseHandbookSingleTitleSubscriptionVariation;
  digitalAccessVariant?: DigitalAccessVariation;
  pseudoStrikePrice?: number;
  relatedProgramVariant?: ProgramVariation;
  isPlusSubscriber?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  printVariants?: CHBVariation[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getRelatedProgramLink = (c: CHBVariation) => (c as any)?.relatedProgramLink;

export const useCHBCatalogRelations = (variant?: CHBVariation): CHBCatalogRelations => {
  const bookProductLink = variant ? getBookProductLink(variant) : undefined;
  const relatedProgramLink = variant ? getRelatedProgramLink(variant) : undefined;

  const query = useProductContentQuery();
  const {
    data: bookProduct,
    isFetching: isBookProductFetching,
    isError: isBookContentError
  } = useGetContentQuery(bookProductLink ? { contentLink: bookProductLink, query } : skipToken);
  const {
    data: relatedProgramVariant,
    isFetching: isRelatedProgramFetching,
    isError: isRelatedProgramVariantContentError
  } = useGetContentQuery(relatedProgramLink ? { contentLink: relatedProgramLink } : skipToken);
  const {
    children,
    isLoading: isBookProductChildrenFetching,
    isApiError: isChildrenError
  } = useGetAllChildren({
    contentLink: bookProduct?.contentLink?.guidValue,
    enableWMSPrice: 'all'
  });
  const printVariant = children?.find(v => isCHBPrintVariant(v) && !getExpiredStatus(v));
  const stsVariant = children?.find(v => isCHBSTSVariant(v) && !getExpiredStatus(v));

  const printVariants = useMemo(() => [printVariant].filter(isDefined), [printVariant]);

  const expiredPrintVariants = useMemo(
    () =>
      (children?.filter(v => isCHBPrintVariant(v) && getExpiredStatus(v)) ?? []) as CHBVariation[],
    [children]
  );

  const pseudoStrikePrice = getVariantPrice(printVariant) ?? getVariantPrice(stsVariant);

  const digitalAccessVariant = useMemo(
    () =>
      bookProduct &&
      children &&
      createDigitalAccessVariantIfEligible(
        bookProduct,
        printVariant ? [printVariant] : undefined,
        stsVariant
      ),
    [bookProduct, children, printVariant, stsVariant]
  );

  const catalogRelations = useMemo<CHBCatalogRelations>(
    () => ({
      bookProduct,
      printVariant,
      printVariants,
      expiredPrintVariants,
      stsVariant,
      digitalAccessVariant,
      relatedProgramVariant,
      pseudoStrikePrice,
      isPlusSubscriber: getIsPlusSubscriber(bookProduct),
      isLoading: isBookProductFetching || isRelatedProgramFetching || isBookProductChildrenFetching,
      isError: !!isBookContentError || !!isRelatedProgramVariantContentError || !!isChildrenError
    }),
    [
      bookProduct,
      digitalAccessVariant,
      isBookContentError,
      isBookProductChildrenFetching,
      isBookProductFetching,
      isChildrenError,
      isRelatedProgramFetching,
      isRelatedProgramVariantContentError,
      printVariant,
      printVariants,
      expiredPrintVariants,
      pseudoStrikePrice,
      relatedProgramVariant,
      stsVariant
    ]
  );

  return catalogRelations;
};
